import {validateOrderableScene} from 'app/components/OrderImageryModal/orderImageryUtils';
import {
  CartItemSnapshot,
  CartItemV1,
  cartTotalCents,
  formatAcresLabel,
  sceneKey,
} from 'app/modules/Cart';
import type {
  ApiImageryCheckoutItemError,
  ApiImageryCheckoutItemErrorCode,
  ApiImageryCheckoutResult,
} from 'app/modules/Remote/api';
import type {ApiOrderableScene} from 'app/modules/Remote/Feature';
import * as conversionUtils from 'app/utils/conversionUtils';
import {formatCost} from 'app/utils/costUtils';

import type {CartContractGroup} from './grouping';
import type {
  AcceptedQuote,
  CartItemQuote,
  CheckoutGroupResult,
  FixPlan,
  GroupFailure,
  ItemFacts,
  ItemRejections,
  ItemValidation,
  ItemValidationStatus,
  OrderedLine,
  QuoteChange,
  SceneAcres,
} from './types';

export const UNORDERABLE_REASONS = {
  featureMissing: 'This property is no longer available.',
  sceneGone: 'This scene is no longer offered for this property.',
  alreadyOrdered: 'This scene has already been ordered for this property.',
  noPrice: 'This source can’t be ordered right now.',
  geometryProblem: 'There’s a problem with this scene’s geometry.',
  nothingLeft: 'The rest of this scene has already been ordered for this property.',
};

export type SceneEligibility =
  | {ok: true; centsPerAcre: number; acres: SceneAcres}
  | {ok: false; reason: string};

/**
 * Whether a scene can be ordered at all, and on what terms. Every surface that puts a scene in the
 * cart asks here, so the cart page never inherits a scene it would then refuse to check out.
 */
export function sceneEligibility(
  scene: ApiOrderableScene,
  centsPerAcre: number | null,
  acres: SceneAcres | null
): SceneEligibility {
  const sceneError = validateOrderableScene(scene).error;
  if (sceneError) {
    return {ok: false, reason: sceneError};
  }
  if (scene.isFullyOrdered) {
    return {ok: false, reason: UNORDERABLE_REASONS.alreadyOrdered};
  }
  if (centsPerAcre === null) {
    return {ok: false, reason: UNORDERABLE_REASONS.noPrice};
  }
  // getOrderGeometryAcres answers -1 when its turf math throws, and the source minimum can floor
  // that back above zero, so the raw area is what says whether the geometry is usable.
  if (acres === null || acres.orderGeometryAcres < 0) {
    return {ok: false, reason: UNORDERABLE_REASONS.geometryProblem};
  }
  if (acres.billableAcres <= 0) {
    return {ok: false, reason: UNORDERABLE_REASONS.nothingLeft};
  }
  return {ok: true, centsPerAcre, acres};
}

/**
 * What a row says about a scene the server turned down. The server's own messages name internals
 * (source ids, "the cart"), so the code picks the sentence and the item supplies the names and the
 * figure the quote moved from.
 */
export function rejectionMessage(error: ApiImageryCheckoutItemError, item: CartItemV1): string {
  const source = item.snapshot.sourceDisplayName;
  switch (error.code) {
    case 'priceChanged':
      return error.centsPerAcre === undefined
        ? 'The price of this scene has changed.'
        : `Price changed: ${formatCost(item.centsPerAcre)} → ${formatCost(error.centsPerAcre)} per acre.`;
    case 'acresMismatch':
      return error.billableAcres === undefined
        ? 'The billable area of this scene has changed.'
        : `Billable area changed: ${conversionUtils.numberWithCommas(item.billableAcres)} → ${formatAcresLabel(error.billableAcres)}.`;
    case 'alreadyPurchased':
      return 'Already ordered — you won’t be charged twice.';
    case 'duplicateInCart':
      return 'This scene is in your cart twice.';
    case 'sourceDisabled':
      return `${source} orders are paused right now. Try again later.`;
    case 'invalidScene':
      return 'This scene is no longer available.';
    case 'notFound':
      return 'We couldn’t find this property. Reload the page.';
    case 'limitReached':
      return 'This property has no scene orders left.';
    case 'sourceNotConfigured':
      return `${source} isn’t available for ordering on your account.`;
    case 'invalidSource':
      return `${source} can’t be ordered.`;
  }
}

/**
 * The order endpoint accepts a 20-acre gap between its area math and ours and bills the requested
 * acreage, so anything smaller isn't a re-quote — flagging it would churn rows over rounding noise.
 */
const ACRES_TOLERANCE = 20;

/** Nearmap costs enough per acre that the endpoint allows it no discrepancy at all. */
const STRICT_ACRES_SOURCE_IDS: ReadonlySet<string> = new Set(['NEARMAP-15']);

const acresDrifted = (sourceId: string, from: number, to: number): boolean =>
  STRICT_ACRES_SOURCE_IDS.has(sourceId) ? from !== to : Math.abs(to - from) >= ACRES_TOLERANCE;

const nextSnapshot = (
  current: CartItemSnapshot,
  fresh: Partial<CartItemSnapshot> | undefined
): CartItemSnapshot | null => {
  if (!fresh) {
    return null;
  }
  const merged: CartItemSnapshot = {
    ...current,
    ...(fresh.featureName !== undefined ? {featureName: fresh.featureName} : {}),
    ...(fresh.projectName !== undefined ? {projectName: fresh.projectName} : {}),
    ...(fresh.sourceDisplayName !== undefined ? {sourceDisplayName: fresh.sourceDisplayName} : {}),
  };
  // The override flag describes the url, so the two move together; the flag is only ever stored
  // as true, never false.
  if (fresh.thumbnailUrl !== undefined) {
    merged.thumbnailUrl = fresh.thumbnailUrl;
    if (fresh.thumbnailIsOverride) {
      merged.thumbnailIsOverride = true;
    } else {
      delete merged.thumbnailIsOverride;
    }
  }
  const keys = new Set([...Object.keys(merged), ...Object.keys(current)]);
  const changed = [...keys].some(
    (key) => merged[key as keyof CartItemSnapshot] !== current[key as keyof CartItemSnapshot]
  );
  return changed ? merged : null;
};

/**
 * Turns one row's refreshed facts into the state its row renders and the patch the stored cart
 * takes. Drift is measured against `originalQuote` rather than the stored item so a row keeps
 * showing what its price moved from after the new one has been written back.
 */
export function reduceItemValidation(
  item: CartItemV1,
  originalQuote: CartItemQuote,
  facts: ItemFacts
): ItemValidation {
  const unorderable = (reason: string): ItemValidation => ({
    status: {kind: 'unorderable', reason},
    patch: null,
  });

  if (facts.featureMissing) {
    return unorderable(UNORDERABLE_REASONS.featureMissing);
  }
  if (facts.scene === undefined || facts.centsPerAcre === undefined || facts.acres === undefined) {
    return {status: {kind: 'validating'}, patch: null};
  }
  if (facts.scene === null) {
    return unorderable(UNORDERABLE_REASONS.sceneGone);
  }
  const eligibility = sceneEligibility(facts.scene, facts.centsPerAcre, facts.acres);
  if (!eligibility.ok) {
    return unorderable(eligibility.reason);
  }

  const {centsPerAcre, acres} = eligibility;
  const changes: QuoteChange[] = [];
  if (centsPerAcre !== originalQuote.centsPerAcre) {
    changes.push({kind: 'price', from: originalQuote.centsPerAcre, to: centsPerAcre});
  }
  if (acresDrifted(item.sourceId, originalQuote.billableAcres, acres.billableAcres)) {
    changes.push({kind: 'acres', from: originalQuote.billableAcres, to: acres.billableAcres});
  }

  const patch: Partial<Omit<CartItemV1, 'id'>> = {};
  if (centsPerAcre !== item.centsPerAcre) {
    patch.centsPerAcre = centsPerAcre;
  }
  if (acresDrifted(item.sourceId, item.billableAcres, acres.billableAcres)) {
    patch.billableAcres = acres.billableAcres;
  }
  const snapshot = nextSnapshot(item.snapshot, facts.snapshot);
  if (snapshot) {
    patch.snapshot = snapshot;
  }

  const status: ItemValidationStatus =
    changes.length > 0 ? {kind: 'updated', changes} : {kind: 'ok'};

  return {status, patch: Object.keys(patch).length > 0 ? patch : null};
}

/**
 * Records what the server said about a scene it turned down, against whatever the refresh pass was
 * saying at the time. Returns null for the codes that carry no corrected value.
 */
export function acceptedQuoteFrom(
  error: ApiImageryCheckoutItemError,
  facts: ItemFacts | undefined
): AcceptedQuote | null {
  if (error.code === 'priceChanged' && error.centsPerAcre !== undefined) {
    return typeof facts?.centsPerAcre === 'number'
      ? {centsPerAcre: {accepted: error.centsPerAcre, superseded: facts.centsPerAcre}}
      : null;
  }
  if (error.code === 'acresMismatch' && error.billableAcres !== undefined) {
    return facts?.acres
      ? {billableAcres: {accepted: error.billableAcres, superseded: facts.acres.billableAcres}}
      : null;
  }
  return null;
}

/**
 * Lets an accepted re-quote stand against the refresh pass, which would otherwise write the rejected
 * value straight back and resubmit exactly what the server just refused. The override lapses as soon
 * as the refresh pass moves off the value the server contradicted, so a genuinely newer price still
 * wins and nothing stale is left pinned.
 */
export function applyAcceptedQuote(facts: ItemFacts, accepted: AcceptedQuote): ItemFacts {
  const priceStands =
    accepted.centsPerAcre !== undefined && facts.centsPerAcre === accepted.centsPerAcre.superseded;
  const acresStand =
    accepted.billableAcres !== undefined &&
    facts.acres != null &&
    facts.acres.billableAcres === accepted.billableAcres.superseded;

  if (!priceStands && !acresStand) {
    return facts;
  }
  return {
    ...facts,
    centsPerAcre: priceStands ? accepted.centsPerAcre!.accepted : facts.centsPerAcre,
    acres: acresStand
      ? {...facts.acres!, billableAcres: accepted.billableAcres!.accepted}
      : facts.acres,
  };
}

/** The corrected value a rejection carries, on the only two codes that carry one. */
export function requotePatch(
  error: ApiImageryCheckoutItemError
): Partial<Omit<CartItemV1, 'id'>> | null {
  if (error.code === 'priceChanged' && error.centsPerAcre !== undefined) {
    return {centsPerAcre: error.centsPerAcre};
  }
  if (error.code === 'acresMismatch' && error.billableAcres !== undefined) {
    return {billableAcres: error.billableAcres};
  }
  return null;
}

/**
 * Everything "Apply all fixes" would do: drop what can't be ordered, accept every re-quote. Covers
 * rows blocked by the refresh pass as well as rows the server rejected, since both block submit.
 */
export function planFixes(
  items: CartItemV1[],
  validations: ReadonlyMap<string, ItemValidation>,
  rejections: ItemRejections
): FixPlan {
  const plan: FixPlan = {removeItemIds: [], requotes: []};
  for (const item of items) {
    const rejection = rejections.get(item.id);
    if (rejection) {
      const patch = requotePatch(rejection);
      if (patch) {
        plan.requotes.push({itemId: item.id, patch});
      } else {
        plan.removeItemIds.push(item.id);
      }
      continue;
    }
    if (validations.get(item.id)?.status.kind === 'unorderable') {
      plan.removeItemIds.push(item.id);
    }
  }
  return plan;
}

/**
 * A row removed by its × is undoable for 5 seconds and can come back with the same item id, so
 * rejections are scoped to what's currently in the cart rather than dropped on removal: hiding a
 * rejection this way makes it reappear automatically if the removal is undone.
 */
export function rejectionsInCart(items: CartItemV1[], rejections: ItemRejections): ItemRejections {
  const ids = new Set(items.map((item) => item.id));
  return new Map([...rejections].filter(([id]) => ids.has(id)));
}

export const rejectionsByItemId = (
  results: CheckoutGroupResult[]
): Map<string, ApiImageryCheckoutItemError> => {
  const map = new Map<string, ApiImageryCheckoutItemError>();
  for (const {outcome} of results) {
    if (outcome.kind === 'failure' && outcome.failure.kind === 'itemRejections') {
      for (const {item, error} of outcome.failure.rejections) {
        map.set(item.id, error);
      }
    }
  }
  return map;
};

/**
 * Whether a failed submit still describes the cart in front of the user. Without this a group-level
 * callout outlives the repair that fixed it, leaving a blocking-looking error beside an enabled
 * Submit button.
 *
 * Only conditions the page can check for itself clear automatically. An `nteExceeded` can't be
 * re-derived — the server sends no numbers and our own math said the group fit, or Submit would
 * have been blocked — so shedding value from the group is the only evidence available that the user
 * has acted on it.
 */
export function failedResultStillApplies(
  result: CheckoutGroupResult,
  group: CartContractGroup | undefined,
  rejections: ItemRejections
): boolean {
  if (result.outcome.kind !== 'failure' || group === undefined) {
    return false;
  }
  switch (result.outcome.failure.kind) {
    // Its rejections are pruned row by row as they're repaired. Once none are left, the submit it
    // described is history — including the errors that named scenes nobody submitted.
    case 'itemRejections':
      return result.outcome.failure.rejections.some(({item}) => rejections.has(item.id));
    case 'nteExceeded':
      return group.subtotalCents >= cartTotalCents(result.submitted);
    case 'noContract':
      return group.contract === null;
    // Removing or re-quoting a row changes none of these, so they stand until the next submit
    // answers differently.
    default:
      return true;
  }
}

/** Group-wide failures only: per-item rejections are shown on their rows instead. */
export const groupFailures = (results: CheckoutGroupResult[]): GroupFailure[] =>
  results.flatMap(({groupKey, label, outcome}) =>
    outcome.kind === 'failure' && outcome.failure.kind !== 'itemRejections'
      ? [{groupKey, label, failure: outcome.failure}]
      : []
  );

/** Errors naming a scene that wasn't submitted. Nothing can act on them, but hiding them lies. */
export const unmatchedErrors = (results: CheckoutGroupResult[]): ApiImageryCheckoutItemError[] =>
  results.flatMap(({outcome}) =>
    outcome.kind === 'failure' && outcome.failure.kind === 'itemRejections'
      ? outcome.failure.unmatched
      : []
  );

export function joinOrderedItems(
  result: ApiImageryCheckoutResult,
  submitted: CartItemV1[]
): OrderedLine[] {
  const bySceneKey = new Map(submitted.map((item) => [sceneKey(item), item]));
  return result.items.map((ordered) => ({ordered, item: bySceneKey.get(sceneKey(ordered))}));
}

/** Codes that should be unreachable once the cart provider is the only way to build a checkout. */
export const IMPOSSIBLE_ITEM_ERROR_CODES: ReadonlySet<ApiImageryCheckoutItemErrorCode> = new Set([
  'duplicateInCart',
]);
